import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { StatusCode, REACT_QUERY_KEYS } from '@hungryroot/rest';
import { getFavoritePairings } from '@hungryroot/rest/api/v2/customers/[id]/favorite_pairings/get';

type OptionType = Pick<UseQueryOptions, 'enabled'>;

export const getFavoritePairingsQueryKey = (customerId: number) => [
  REACT_QUERY_KEYS.CUSTOMERS,
  customerId,
  REACT_QUERY_KEYS.FAVORITE_PAIRINGS,
];

function useFavoritePairings(customerId: number, options: OptionType = {}) {
  return useQuery({
    queryKey: getFavoritePairingsQueryKey(customerId),
    queryFn: async () => {
      const resp = await getFavoritePairings({ customerId });

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useFavoritePairing(
  customerId: number,
  pairingId: number,
  options: OptionType = {}
) {
  let isFavorited = false;

  const result = useFavoritePairings(customerId, options);

  if (!result.isLoading && result.data && result.data.favorite_pairings) {
    isFavorited =
      result.data.favorite_pairings.findIndex(
        (p) => p.pairing_id === pairingId
      ) >= 0;
  }

  return { ...result, isFavorited };
}
